import { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Axios, { CancelTokenSource, AxiosRequestConfig, CancelToken } from 'axios';

import {
  KeyResultsResponse,
  ObjectiveI,
  ObjectivesResponse,
} from '~src/store/okrs/keyResultStream/interfaces';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { CardsContainer } from '~src/modules/questions/components/shared.sc';
import { Centering } from '~src/modules/questions/containers/questions.sc';
import PageLoader from '~src/shared/pageLoader/PageLoader';
import { baseBlue } from '~src/styles/colors';
import { translateContent } from '~src/services/translationServices';
import { fetchObjectivesA, getObjective } from '~src/store/okrs/actions';
import { ObjectiveFilterTypes, ObjectivesQueryI } from '~src/store/okrs/interfaces';
import {
  fetchKeyResults,
  KeyResultFilterTypes,
  KeyResultQueryI,
} from '~src/store/okrs/keyResults/actions';
import PageNotFound from '~src/shared/errorPages/PageNotFound';

import MoreToUpdateSuper from './moreToUpdate/MoreToUpdate';
import NoMoreToUpdateSuper from './noMoreToUpdate/NoMoreToUpdate';

interface DispatchProps {
  fetchKeyResults(
    objectiveId: string,
    query?: KeyResultQueryI,
    config?: AxiosRequestConfig,
  ): ApiReduxResp<KeyResultsResponse>;
  fetchObjectives(queryObj?: ObjectivesQueryI, config?: AxiosRequestConfig): ApiReduxResp<ObjectivesResponse>;
  fetchObjectiveDetails(objectiveId: string, config?: AxiosRequestConfig): ApiReduxResp<ObjectiveI>;
}

interface OwnProps {
  objectiveId: string;
  nextObjectiveId?: string;
}

export interface NextObjective extends ObjectiveI {
  keyResultsAmount: number;
}

let cancelTokenSource: CancelTokenSource | null;
type Props = DispatchProps & OwnProps;

const FinishedObjectiveStream: FC<Props> = props => {
  const [nextObjectiveLoading, setNextObjectiveLoading] = useState(true);
  const [nextObjective, setNextObjective] = useState<NextObjective | null>(null);
  const [currObjectiveLoading, setCurrObjectiveLoading] = useState(true);
  const [currObjective, setCurrObjective] = useState<ObjectiveI | null>(null);

  const fetchCurrentObjective = async (cancelToken: CancelToken) => {
    try {
      const res = await props.fetchObjectiveDetails(props.objectiveId, { cancelToken });
      setCurrObjective(res.response.data);
    } catch (err) {
      // todo: show 'try again'
    }
    setCurrObjectiveLoading(false);
  };

  const fetchNextObjectives = async (cancelToken: CancelToken) => {
    try {
      const res = await props.fetchObjectives({ [ObjectiveFilterTypes.Stream]: 'true' }, { cancelToken });
      const objectives = res.response.data.items;
      // if current objective is the only one available, if the user skipped it we shouldn't show it again) {
      if (objectives.length > 1 || (objectives.length === 1 && objectives[0].id !== props.objectiveId)) {
        // if after fetching the objectives the supposed next objective doesn't exist we simply put the first one as next
        const nextObjectiveInList: ObjectiveI =
          objectives.find((o: ObjectiveI) => o.id === props.nextObjectiveId) || objectives[0].id;
        // load nextObjectiveId keyResults to figure out how many key results to update
        const { response } = await props.fetchKeyResults(
          nextObjectiveInList.id,
          { [KeyResultFilterTypes.Stream]: 'true' },
          { cancelToken },
        );
        setNextObjective({
          id: nextObjectiveInList.id,
          name: nextObjectiveInList.name,
          progress: nextObjectiveInList.progress,
          keyResultsAmount: response.data.length,
        });
      }
    } catch (err) {
      // console.log('oops! fetching objectives failed', err);
    }
    setNextObjectiveLoading(false);
  };

  useEffect(() => {
    cancelTokenSource = Axios.CancelToken.source();
    if (!currObjective) {
      fetchCurrentObjective(cancelTokenSource.token);
    }

    if (props.nextObjectiveId) {
      fetchNextObjectives(cancelTokenSource.token);
    } else {
      // TODO check for initial Loading
      // setNextObjectiveState({ loading: false, initialLoaded: true });
    }
    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel('FINISHED OBJECTIVE STREAM');
      }
    };
  }, []);

  if (currObjectiveLoading || nextObjectiveLoading) {
    return (
      <CardsContainer>
        <Centering>
          <PageLoader secondColor={baseBlue.a5} />
        </Centering>
      </CardsContainer>
    );
  }

  if (!currObjective) {
    return <PageNotFound />;
  }

  if (nextObjective) {
    return <MoreToUpdateSuper currObjective={currObjective} nextObjective={nextObjective} />;
  }

  return (
    <NoMoreToUpdateSuper progress={currObjective.progress} name={translateContent(currObjective.name)} />
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchKeyResults: (objectiveId, query, config) => dispatch(fetchKeyResults(objectiveId, query, config)),
  fetchObjectives: (query, config) => dispatch(fetchObjectivesA(query, config)),
  fetchObjectiveDetails: (objectiveId, config) => dispatch(getObjective(objectiveId, config)),
});

const FinishedObjectiveStreamSuper = connect(null, mapDispatchToProps)(FinishedObjectiveStream);

export default FinishedObjectiveStreamSuper;
